import { Feature, FeatureCollection } from "geojson";
import { groupBy } from "lodash";
import {
  Field,
  filterFieldsWithNotDeletedBoundaries,
  getAllFieldGeomsFromFields,
  getAnimalGroupColorMappingFromArray,
  getFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray,
  getFieldsWithAnimalsCentersFromArray,
  getFieldsWithNoTilesForNotDeletedBoundariesFromArray,
  mapFieldsCentersByIdFromArray,
  useCurrentFarmAnimalGroups,
  useFields,
} from "model";
import { MutableRefObject, useMemo } from "react";
import { Layer, MapRef, Marker, Source } from "react-map-gl/mapbox";
import { useMatch } from "react-router-dom";
import { useUIStore } from "stores";
import AnimalsLocationMarker from "./AnimalsLocationMarker";
import {
  FIELD_TILE_STATUS_STYLE,
  FieldTileStatusMarker,
  useRecentlyCompletedTileFields,
} from "./FieldTileStatusMarker";

interface Props {
  editedFieldId?: string;
  fieldId?: string;
  fieldIds?: string[];
  fillLayers?: boolean;
  mapRef: MutableRefObject<MapRef | null>;
  setTooltipOverMarker: (value: boolean) => void;
  /** Current map zoom level - used to show fills when zoomed out (below tile threshold) */
  viewportZoom?: number;
  /** Whether tiles are ready to display - if false, show solid fills even when zoomed in */
  tilesReady?: boolean;
}

export default function FieldGeoJSONLayers({
  editedFieldId,
  fieldId,
  fieldIds,
  fillLayers,
  mapRef,
  setTooltipOverMarker,
  viewportZoom,
  tilesReady,
}: Props) {
  const editMatch = useMatch("/edit-field/:id/*");

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();

  // Use Zustand for UI state
  const tmpDeletedFeatures = useUIStore((state) => state.tmpDeletedFeatures);

  // Use React Query hook for animal groups and compute color mapping
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const colorByAnimalGroupId = useMemo(
    () => getAnimalGroupColorMappingFromArray(animalGroups),
    [animalGroups]
  );

  // Compute field geoms from React Query data
  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(allFields),
    [allFields]
  );

  // Compute field centers
  const fieldCentersById = useMemo(
    () => mapFieldsCentersByIdFromArray(allFields),
    [allFields]
  );

  // Get current field geom
  const currentFieldGeom = useMemo((): Feature | undefined => {
    if (!fieldId) return undefined;
    const field = allFields.find((f: Field) => f.guid === fieldId);
    return field?.geom;
  }, [allFields, fieldId]);

  // Filter fields with not-deleted boundaries
  const fieldsWithNotDeletedBoundaries = useMemo(
    () => filterFieldsWithNotDeletedBoundaries(allFields, tmpDeletedFeatures),
    [allFields, tmpDeletedFeatures]
  );

  // Filter fields for display
  const fields = useMemo(
    () =>
      fieldsWithNotDeletedBoundaries.filter(
        (field) =>
          field.guid !== editedFieldId &&
          (!fieldIds || fieldIds.includes(field.guid))
      ),
    [fieldsWithNotDeletedBoundaries, editedFieldId, fieldIds]
  );

  // Group fields by animal group - memoize to prevent recalculation on every render
  const fieldsByAnimalGroups = useMemo(
    () => groupBy(fields, "animalGroup"),
    [fields]
  );

  const filteredFieldGeoms = useMemo(() => {
    // If no fieldIds provided, return all fields
    if (!fieldIds || fieldIds.length === 0) return fieldGeoms;

    // Otherwise filter to only show specified fields
    return {
      type: "FeatureCollection",
      features: fieldGeoms.features.filter((feature) =>
        fieldIds.includes(feature.properties?.id as string)
      ),
    };
  }, [fieldGeoms, fieldIds]);

  // Compute fields with no tiles from React Query data
  const fieldsWithNoTiles = useMemo(() => {
    const noTilesFields = getFieldsWithNoTilesForNotDeletedBoundariesFromArray(
      allFields,
      tmpDeletedFeatures
    );
    return noTilesFields.filter((fieldGuid) => {
      // Always exclude edited field
      if (fieldGuid === editedFieldId) return false;

      // If fieldIds is provided, only include fields from that list
      if (fieldIds && fieldIds.length > 0) {
        return fieldIds.includes(fieldGuid);
      }

      // Otherwise include all fields
      return true;
    });
  }, [allFields, tmpDeletedFeatures, editedFieldId, fieldIds]);

  // Compute field centers with no tiles from React Query data
  const fieldsWithNoTilesCenters = useMemo(() => {
    const centers = getFieldsCenterWithNoTilesForNotDeletedBoundariesFromArray(
      allFields,
      tmpDeletedFeatures,
      fieldCentersById
    );
    return centers.filter((field) => {
      // Always exclude edited field
      if (field.guid === editedFieldId) return false;

      // If fieldIds is provided, only include fields from that list
      if (fieldIds && fieldIds.length > 0) {
        return fieldIds.includes(field.guid);
      }

      // Otherwise include all fields
      return true;
    });
  }, [
    allFields,
    tmpDeletedFeatures,
    fieldCentersById,
    editedFieldId,
    fieldIds,
  ]);
  const recentlyCompletedTileFields = useRecentlyCompletedTileFields(
    fieldsWithNoTiles,
    fieldCentersById,
    { editedFieldId, fieldIds }
  );

  // Compute fields with animals from React Query data
  const fieldsWithAnimals = useMemo(
    () =>
      getFieldsWithAnimalsCentersFromArray(
        allFields,
        colorByAnimalGroupId,
        fieldCentersById
      ),
    [allFields, colorByAnimalGroupId, fieldCentersById]
  );

  const dryMatterMultiplier = 10000;

  // Show solid fill colors when:
  // 1. On mobile (fillLayers prop is true), OR
  // 2. When tiles are not ready yet (smooth transition - avoid empty fields flash)
  const showSolidFills = fillLayers || !tilesReady;

  return (
    <>
      <Source
        id="field-outlines"
        data={filteredFieldGeoms as FeatureCollection}
        type="geojson"
      >
        <Layer
          id="field-fill"
          type="fill"
          filter={["!=", "id", editMatch?.params.id || ""]}
          paint={{
            "fill-color": showSolidFills
              ? [
                  "case",
                  ["has", "dryMatterNow"],
                  [
                    "case",
                    ["<", ["get", "dryMatterNow"], 0.07 * dryMatterMultiplier],
                    "#A8917B",
                    ["<", ["get", "dryMatterNow"], 0.1 * dryMatterMultiplier],
                    "#CABFA1",
                    ["<", ["get", "dryMatterNow"], 0.115 * dryMatterMultiplier],
                    "#D6D6AD",
                    ["<", ["get", "dryMatterNow"], 0.13 * dryMatterMultiplier],
                    "#D7E7AA",
                    ["<", ["get", "dryMatterNow"], 0.145 * dryMatterMultiplier],
                    "#D1F094",
                    ["<", ["get", "dryMatterNow"], 0.16 * dryMatterMultiplier],
                    "#C5E97C",
                    ["<", ["get", "dryMatterNow"], 0.175 * dryMatterMultiplier],
                    "#B3E579",
                    ["<", ["get", "dryMatterNow"], 0.19 * dryMatterMultiplier],
                    "#A5DE77",
                    ["<", ["get", "dryMatterNow"], 0.205 * dryMatterMultiplier],
                    "#99D775",
                    ["<", ["get", "dryMatterNow"], 0.22 * dryMatterMultiplier],
                    "#8ED173",
                    ["<", ["get", "dryMatterNow"], 0.235 * dryMatterMultiplier],
                    "#82CA71",
                    ["<", ["get", "dryMatterNow"], 0.25 * dryMatterMultiplier],
                    "#76C36F",
                    ["<", ["get", "dryMatterNow"], 0.265 * dryMatterMultiplier],
                    "#6ABC6D",
                    ["<", ["get", "dryMatterNow"], 0.28 * dryMatterMultiplier],
                    "#62B36C",
                    ["<", ["get", "dryMatterNow"], 0.295 * dryMatterMultiplier],
                    "#59AB6C",
                    ["<", ["get", "dryMatterNow"], 0.31 * dryMatterMultiplier],
                    "#52A36B",
                    ["<", ["get", "dryMatterNow"], 0.34 * dryMatterMultiplier],
                    "#4A9A6A",
                    ["<", ["get", "dryMatterNow"], 0.37 * dryMatterMultiplier],
                    "#41916A",
                    ["<", ["get", "dryMatterNow"], 0.4 * dryMatterMultiplier],
                    "#388969",
                    ["<", ["get", "dryMatterNow"], 0.45 * dryMatterMultiplier],
                    "#318168",
                    ["<", ["get", "dryMatterNow"], 0.5 * dryMatterMultiplier],
                    "#1E7662",
                    [
                      "<",
                      ["get", "dryMatterNow"],
                      0.6001 * dryMatterMultiplier,
                    ],
                    "#0E6E5C",
                    "#008000",
                  ],
                  "#FFFF00",
                ]
              : "transparent",
            "fill-opacity": showSolidFills ? 1.0 : 0,
          }}
        />
        <Layer
          id="field-fill-no-data"
          type="fill"
          //only show this if a field has no tiles
          filter={["in", ["get", "id"], ["literal", fieldsWithNoTiles]]}
          paint={{
            "fill-color": FIELD_TILE_STATUS_STYLE.fillColor,
            "fill-opacity": FIELD_TILE_STATUS_STYLE.fillOpacity,
          }}
        />
        <Layer
          id="field-outline-no-data"
          type="line"
          filter={["in", ["get", "id"], ["literal", fieldsWithNoTiles]]}
          paint={{
            "line-color": FIELD_TILE_STATUS_STYLE.lineColor,
            "line-opacity": 0.9,
            "line-width": [
              "interpolate",
              ["exponential", 2],
              ["zoom"],
              10,
              ["*", 10, ["^", 2, -6]],
              24,
              ["*", 10, ["^", 2, 7]],
            ],
            "line-dasharray": [2, 1.4],
          }}
          layout={{
            "line-join": "round",
          }}
        />
        {Object.keys(fieldsByAnimalGroups).map((animalGroupId) => {
          const fieldIdsInAnimalGroup = fieldsByAnimalGroups[animalGroupId].map(
            ({ guid }) => guid
          );
          return (
            <Layer
              key={animalGroupId}
              id={`field-outlines-group-${animalGroupId}`}
              type="line"
              filter={["in", ["get", "id"], ["literal", fieldIdsInAnimalGroup]]}
              paint={{
                "line-color": colorByAnimalGroupId[animalGroupId] || "#666666",
                "line-width": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5, ["^", 2, -6]],
                  24,
                  ["*", 5, ["^", 2, 7]],
                ],
                "line-offset": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5 / 2 - 1, ["^", 2, -6]],
                  24,
                  ["*", 5 / 2 - 1, ["^", 2, 5]],
                ],
              }}
              layout={{
                "line-join": "miter",
              }}
            />
          );
        })}
      </Source>
      {currentFieldGeom &&
        fieldId &&
        fieldId !== editedFieldId &&
        tmpDeletedFeatures.indexOf(fieldId) === -1 && (
          <Source id="field-highlight" data={currentFieldGeom} type="geojson">
            <Layer
              id="field-highlight"
              type="line"
              paint={{
                "line-color": "#FFFFFF",
                "line-width": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5, ["^", 2, -6]],
                  24,
                  ["*", 5, ["^", 2, 7]],
                ],
                "line-offset": [
                  "interpolate",
                  ["exponential", 2],
                  ["zoom"],
                  10,
                  ["*", 5 / 2 - 1, ["^", 2, -6]],
                  24,
                  ["*", 5 / 2 - 1, ["^", 2, 5]],
                ],
              }}
              layout={{
                "line-join": "miter",
              }}
            />
          </Source>
        )}
      {viewportZoom !== undefined &&
        viewportZoom >= 13 &&
        !editedFieldId &&
        fieldsWithAnimals.map((field) => {
          const { coordinates } = field.center.geometry;
          return (
            <AnimalsLocationMarker
              zoom={viewportZoom}
              coordinates={coordinates}
              htmlColor={field.color}
              key={field.guid}
              currentField={field}
              fields={fields}
            />
          );
        })}
      {viewportZoom !== undefined &&
        viewportZoom >= 13 &&
        fieldsWithNoTilesCenters.map((field) => {
          const { coordinates } = field.center.geometry;
          return (
            <Marker
              key={`no-tiles-field-${field.guid}`}
              longitude={coordinates[0]}
              latitude={coordinates[1]}
            >
              <FieldTileStatusMarker status="computing" />
            </Marker>
          );
        })}
      {viewportZoom !== undefined &&
        viewportZoom >= 13 &&
        recentlyCompletedTileFields.map((field) => {
          const { coordinates } = field.center.geometry;
          return (
            <Marker
              key={`done-tiles-field-${field.guid}`}
              longitude={coordinates[0]}
              latitude={coordinates[1]}
            >
              <FieldTileStatusMarker status="done" />
            </Marker>
          );
        })}
    </>
  );
}
